import { useMemo } from 'react';
import type { FilterFieldCatalogEntry } from '@shared/flow/filter-fields';
import { useFlowPlays } from '../plays/hooks';

/**
 * The catalog key A4 turns into the rep-facing "Signal Subtype" picklist's
 * live option list — replaces A1's `signal_instance_id` (retired: no id is
 * ever a rep-facing filter value, see `@shared/flow/plays-view`'s doc
 * comment on `buildPlayScopeGroup`).
 */
const SIGNAL_SUBTYPE_FIELD_KEY = 'signal_subtype';

/**
 * The contact catalog's `signal_subtype` field with the LIVE play NAMES as
 * its options (plan `2026-09-10-001-plays-tab-filter-redesign-plan.md`
 * amendment A4, dispatch plan `2026-09-11-002` unit U12).
 *
 * A4 scopes a play by its NAME, not a record id — the stored filter value
 * and the displayed option text are the SAME string, so `values` here is a
 * plain `string[]` (unlike A1's retired `{ value, label }` pairs). The
 * catalog itself (`@shared/flow/filter-fields`) deliberately ships the
 * `signal_subtype` entry with NO `values`: the option list is live data, and
 * a shared server-side catalog is not where live data belongs.
 *
 * Pure so it can be tested without a network or a React tree, and so the
 * component can memoize it against the names array.
 *
 * Returns the SAME array reference when there is nothing to inject (no
 * names yet, or no `signal_subtype` field in this catalog) — a
 * loading/error state must not churn the identity of the array feeding
 * `FlowTableConfig.filterFields`. Every entry other than `signal_subtype`
 * is passed through by reference.
 */
export function withSignalSubtypeOptions(
  fields: FilterFieldCatalogEntry[],
  names: string[],
): FilterFieldCatalogEntry[] {
  if (names.length === 0) return fields;
  if (!fields.some(f => f.key === SIGNAL_SUBTYPE_FIELD_KEY)) return fields;
  return fields.map(f => (f.key === SIGNAL_SUBTYPE_FIELD_KEY ? { ...f, values: names } : f));
}

/**
 * The live plays' NAMES, deduped, as the `signal_subtype` field's options.
 *
 * Reads the EXISTING `useFlowPlays` query (`../plays/hooks`) rather than a
 * second endpoint: the list is small, it is already cached
 * (`staleTime: Infinity`), and on the Plays surface the plays list has
 * usually loaded it before the rep ever opens the Filters popover, so the
 * options are there for free.
 *
 * Never blocks or errors the table: while loading, on a failed request, or
 * on a route the caller's mock server doesn't handle, this is `[]` and
 * `withSignalSubtypeOptions` leaves the catalog exactly as it was — the
 * Signal Subtype field simply free-types its value until the list arrives.
 *
 * `filters: []` is the A2/U9 spelling of the old `scope: 'all'`: the catalog
 * needs EVERY live play as an option (a rep filtering contacts by "Signal
 * Subtype" must be able to pick a play the Plays list's own chips currently
 * hide), so this query is deliberately unfiltered and shares its cache
 * entry with nothing the Plays list asks for.
 *
 * A26 made the Plays list events-only, so this option list is now event
 * plays only too. That is the honest state of the surface — the Plays list
 * has no Gong rows to emit a preset from — and it goes back to covering
 * every play kind the moment the plays route does.
 *
 * `options.enabled` (A28) — options are only needed where a Plays row can
 * emit a preset, so the caller passes `enabled: mount.surface === 'plays'`.
 * On `people`/account mounts the field free-types, same as its loading state.
 */
export function useSignalSubtypeOptions(options: { enabled: boolean }): string[] {
  const { data } = useFlowPlays({ filters: [] }, { enabled: options.enabled });
  return useMemo(() => {
    const names = (data?.plays ?? [])
      .map(play => play.name)
      .filter((name): name is string => name != null);
    return Array.from(new Set(names));
  }, [data]);
}
